import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import * as Yup from 'yup';
import { DayHoursPicker, Collapse } from '@knockrentals/knock-react';
import PropertyInfoEditCustom from '../Properties/PropertyInfoEditCustom';
import { DraggableListContainer } from './DraggableList/index';
import IntegrationInstructions from './IntegrationInstructions';
import DoorwayColorPickerInput from './components/DoorwayColorPickerInput';
import DoorwayCheckboxInput from './components/DoorwayCheckboxInput';
import { LinkedProperties } from './components/LinkedProperties';
import { FeatureFlagContext } from '../../Context/FeatureFlagContext';
import WebsiteUrl from './components/WebsiteUrl';
import './PropertyDoorway.scss';

const virtualTourLinksValidator = Yup.object().shape({
  name: Yup.string()
    .required('Name is a required field')
    .typeError('Name must be a string'),
  url: Yup.string()
    .matches(
      /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-/]))?/,
      'URL must begin with http:// or https://'
    )
    .required('URL is a required field')
    .typeError('URL must be a string'),
});

class PropertyDoorway extends Component {
  state = {
    hourOptions: [],
    preferences: {},
    recaptchaErrors: {},
    recaptchaFields: {
      recaptcha_public_key: '',
      recaptcha_score_threshold: '',
    },
    templates: {},
  };

  componentDidMount() {
    const dayStart = moment().hour(7).minute(0).second(0);
    const dayEnd = moment().hour(22).minute(0).second(0);
    const timeWalker = moment(dayStart);

    const hourOptions = [];

    while (timeWalker.isBefore(dayEnd)) {
      hourOptions.push(timeWalker.format('HH:mm:00'));
      timeWalker.add(15, 'minutes');
    }

    this.setState({
      hourOptions,
      preferences: {
        button_greeting:
          this.props.doorwaySettings.settings.preferences.button_greeting,
      },
      recaptchaFields: {
        recaptcha_public_key:
          this.props.doorwaySettings.settings.preferences.recaptcha_public_key || '',
        recaptcha_score_threshold:
          this.props.doorwaySettings.settings.preferences.recaptcha_score_threshold || '',
      },
      templates: {
        greeting:
          this.props.doorwaySettings.settings.message_templates.greeting,
        guest: this.props.doorwaySettings.settings.message_templates.guest,
        away: this.props.doorwaySettings.settings.message_templates.away,
        away_response:
          this.props.doorwaySettings.settings.message_templates.away_response,
      },
    });
  }

  render() {
    const websiteUrl = this.props.doorwaySettings.website;
    const { recaptcha_public_key, recaptcha_score_threshold } =
      this.state.recaptchaFields;
    const { preferences } = this.props.doorwaySettings.settings;
    const hasRecaptchaErrors = Object.values(this.state.recaptchaErrors).some(Boolean);
    const isRecaptchaDirty =
      recaptcha_public_key !== (preferences.recaptcha_public_key || '') ||
      recaptcha_score_threshold !== (preferences.recaptcha_score_threshold || '');
    const isRecaptchaSaveDisabled = !isRecaptchaDirty || hasRecaptchaErrors;

    return (
      <Collapse title={this.props.doorwaySettings.name}>
        {websiteUrl && <WebsiteUrl url={this.props.doorwaySettings.website} />}

        <div className="doorway-property-content-container">
          <h3>General Settings</h3>

          {this.context.isDoorwayEnhancementsEnabled && (
            <DoorwayCheckboxInput
              checked={
                this.props.property.preferences.disable_pricing_availability
              }
              id="disable_pricing_availability"
              label="Disable Pricing / Availability"
              onChange={this.propertyPreferenceOnChange}
              settingName="disable_pricing_availability"
            />
          )}

          <DoorwayCheckboxInput
            label="Require unit scheduling"
            settingName="requireUnitScheduling"
            checked={this.props.property.preferences.require_unit_scheduling}
            onChange={this.requireUnitSchedulingChange.bind(this)}
            id="requireUnitScheduling"
          />
          <DoorwayCheckboxInput
            label="Send Google Analytics Events"
            checked={
              this.props.doorwaySettings.settings.preferences
                .emit_analytics_events
            }
            onChange={this.sendGoogleAnalyticsEventsChanged}
            id="analytics"
          />
          <DoorwayCheckboxInput
            label="Send Google Tag Manager Events"
            checked={
              this.props.doorwaySettings.settings.preferences
                .emit_tag_manager_events
            }
            onChange={this.sendGoogleTagManagerEventsChanged}
            id="tag-manager"
          />
          <Collapse title="reCAPTCHA Settings">
            <div className="doorway-property-setting-item">
              <label htmlFor="recaptcha_public_key">reCAPTCHA Site Key</label>
              <div className="recaptcha-field-wrapper">
                <input
                  id="recaptcha_public_key"
                  name="recaptcha_public_key"
                  type="text"
                  onBlur={this.handleRecaptchaBlur}
                  onChange={this.handleRecaptchaChange}
                  onFocus={this.handleRecaptchaFocus}
                  value={this.state.recaptchaFields.recaptcha_public_key}
                />
                {this.state.recaptchaErrors.recaptcha_public_key && (
                  <span className="recaptcha-error">
                    {this.state.recaptchaErrors.recaptcha_public_key}
                  </span>
                )}
              </div>
            </div>
            <div className="doorway-property-setting-item">
              <label htmlFor="recaptcha_score_threshold">reCAPTCHA Score Threshold</label>
              <div className="recaptcha-field-wrapper">
                <input
                  id="recaptcha_score_threshold"
                  name="recaptcha_score_threshold"
                  type="text"
                  onBlur={this.handleRecaptchaBlur}
                  onChange={this.handleRecaptchaChange}
                  onFocus={this.handleRecaptchaFocus}
                  value={this.state.recaptchaFields.recaptcha_score_threshold}
                  placeholder="0.5"
                />
                {this.state.recaptchaErrors.recaptcha_score_threshold && (
                  <span className="recaptcha-error">
                    {this.state.recaptchaErrors.recaptcha_score_threshold}
                  </span>
                )}
              </div>
            </div>
            <button
              className="recaptcha-save-button knock-react-button"
              disabled={isRecaptchaSaveDisabled}
              onClick={this.handleRecaptchaSubmit}
              type="button"
            >
              Save reCAPTCHA Settings
            </button>
          </Collapse>
          <hr />
          <h3>Button Settings</h3>
          <DoorwayCheckboxInput
            label="Display button in bottom right"
            checked={
              this.props.doorwaySettings.settings.preferences
                .display_launcher_button
            }
            onChange={this.displayButtonChanged.bind(this)}
            id="tag-manager"
          />
          <DoorwayColorPickerInput
            label="Button color"
            color={this.props.doorwaySettings.settings.preferences.button_color}
            onChange={this.buttonColorChanged}
            testId="color-picker-button"
          />
          <DoorwayColorPickerInput
            label="Theme color"
            color={
              this.props.doorwaySettings.settings.preferences.primary_color
            }
            onChange={this.primaryColorChanged}
            testId="color-picker-theme"
          />
          <div className="doorway-property-setting-item">
            <div>
              <span>Button greeting text</span>
            </div>
            <textarea
              value={this.state.preferences.button_greeting}
              onChange={this.handleButtonGreetingChange}
              onBlur={this.handleButtonGreetingSubmit}
            />
          </div>
          <hr />
          <h3>Hours</h3>
          <div>{this.renderHours()}</div>
          <hr />
          {this.context.isMultiPhasePropertyEnabled && (
            <React.Fragment>
              <LinkedProperties
                fetchNearbyProperties={this.props.fetchNearbyProperties}
                onDoorwayChanged={this.props.onDoorwayChanged}
                propertyId={this.props.doorwaySettings.id}
                settings={this.props.doorwaySettings.settings || {}}
                teamProperties={this.props.teamProperties}
              />
              <hr />
            </React.Fragment>
          )}
          <div>
            <PropertyInfoEditCustom
              isChatEnabled={this.props.isChatEnabled}
              mergeTags={this.props.mergeTags}
              onChange={this.props.onPropertyChanged}
              property={this.props.property}
            />
          </div>
          <hr />
          <h3>Virtual Tours Settings</h3>
          <div>
            <div className="doorway-property-setting-item">
              <label
                className="doorway-label-text"
                htmlFor="enableVirtualTourLinks"
              >
                Virtual Tour Links
              </label>
              <input
                id="enableVirtualTourLinks"
                type="checkbox"
                checked={
                  this.props.schedulingSettings.preferences.virtual_tour_links
                }
                onChange={this.virtualTourLinksChange.bind(this)}
              />
            </div>

            <div className="doorway-help-text">
              Add up to 10 Virtual Tours to Doorway. Reorder items to customize
              display order. To improve accessibility and support ADA
              compliance, please upload videos with subtitles or some form of
              descriptive narration.
            </div>
          </div>
          <DraggableListContainer
            data={
              this.props.schedulingSettings.preferences
                .virtual_tour_links_mapping
            }
            fields={['name', 'url']}
            headers={['Name', 'Url']}
            maxSize={10}
            onDataChange={this.virtualTourLinksMappingChange.bind(this)}
            validator={virtualTourLinksValidator}
          />
          <hr />
          <IntegrationInstructions
            snippetConfig={this.props.doorwaySettings.settings.snippetConfig}
          />
        </div>
      </Collapse>
    );
  }

  // A temporary feature flag that will be removed when the feature is fully
  // exposed to users, see: https://knockr.atlassian.net/browse/SP-1255
  isVirtualTourFeatureFlagActive() {
    return (
      this.props.schedulingSettings &&
      this.props.schedulingSettings.preferences &&
      this.props.schedulingSettings.preferences.virtual_tour_links &&
      this.props.schedulingSettings.preferences.virtual_tour_links_mapping
    );
  }

  buttonColorChanged = (color) => {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.button_color = color;
    this.settingsUpdated(updatedSettings);
  };

  handleButtonGreetingChange = (event) => {
    this.setState({
      preferences: {
        button_greeting: event.target.value,
      },
    });
  };

  handleButtonGreetingSubmit = () => {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.button_greeting =
      this.state.preferences.button_greeting;
    this.settingsUpdated(updatedSettings);
  };

  primaryColorChanged = (color) => {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.primary_color = color;
    this.settingsUpdated(updatedSettings);
  };

  displayButtonChanged(event) {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.display_launcher_button = event.target.checked;
    this.settingsUpdated(updatedSettings);
  }

  sendGoogleTagManagerEventsChanged = (event) => {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.emit_tag_manager_events = event.target.checked;
    updatedSettings.preferences.emit_analytics_events = false;

    this.settingsUpdated(updatedSettings);
  };

  sendGoogleAnalyticsEventsChanged = (event) => {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.preferences.emit_analytics_events = event.target.checked;
    updatedSettings.preferences.emit_tag_manager_events = false;

    this.settingsUpdated(updatedSettings);
  };

  handleRecaptchaChange = (event) => {
    const { name, value } = event.target;

    this.setState((prevState) => ({
      recaptchaErrors: {},
      recaptchaFields: {
        ...prevState.recaptchaFields,
        [name]: value,
      },
    }));
  };

  handleRecaptchaFocus = (event) => {
    const { name } = event.target;

    this.setState((prevState) => ({
      recaptchaErrors: {
        ...prevState.recaptchaErrors,
        [name]: undefined,
      },
    }));
  };

  handleRecaptchaBlur = (event) => {
    const { name, value } = event.target;

    this.setState((prevState) => ({
      recaptchaFields: {
        ...prevState.recaptchaFields,
        [name]: value.trim(),
      },
    }));
  };

  handleRecaptchaSubmit = () => {
    const { recaptcha_public_key, recaptcha_score_threshold } =
      this.state.recaptchaFields;

    const errors = {};

    if (recaptcha_score_threshold) {
      const threshold = parseFloat(recaptcha_score_threshold);

      if (isNaN(threshold) || threshold < 0 || threshold > 1) {
        errors.recaptcha_score_threshold =
          'reCAPTCHA Score Threshold must be a number between 0.0 and 1.0';
      }
    }

    if (Object.keys(errors).length > 0) {
      this.setState({ recaptchaErrors: errors });
      return;
    }

    this.setState({ recaptchaErrors: {} });

    const updatedSettings = {
      ...this.props.doorwaySettings.settings,
      preferences: {
        ...this.props.doorwaySettings.settings.preferences,
        recaptcha_public_key,
        recaptcha_score_threshold,
      },
    };
    this.settingsUpdated(updatedSettings);
  };

  renderHours() {
    return new Array(7).fill(0).map((_, index) => {
      const weekday = index + 1;
      const dayHours = this.props.doorwaySettings.settings.hours[weekday];

      const startTimeChanged = (event) => {
        const updatedHours = this.props.doorwaySettings.settings.hours[weekday];
        updatedHours.start_time = event.target.value;

        this.settingsUpdated(this.props.doorwaySettings.settings);
      };

      const endTimeChanged = (event) => {
        const updatedHours = this.props.doorwaySettings.settings.hours[weekday];
        updatedHours.end_time = event.target.value;

        this.settingsUpdated(this.props.doorwaySettings.settings);
      };

      const isActiveChanged = (event) => {
        const updatedHours = this.props.doorwaySettings.settings.hours[weekday];
        updatedHours.is_active = event.target.checked;

        this.settingsUpdated(this.props.doorwaySettings.settings);
      };

      return (
        <DayHoursPicker
          key={weekday}
          weekday={weekday}
          startTime={dayHours.start_time}
          endTime={dayHours.end_time}
          timeOptions={this.state.hourOptions}
          isActive={dayHours.is_active}
          startTimeChanged={startTimeChanged}
          endTimeChanged={endTimeChanged}
          isActiveChanged={isActiveChanged}
        />
      );
    });
  }

  templatesChanged() {
    const updatedSettings = this.props.doorwaySettings.settings;

    updatedSettings.message_templates = this.state.templates;
    this.settingsUpdated(updatedSettings);
  }

  settingsUpdated(updatedSettings) {
    this.props.onDoorwayChanged(this.props.doorwaySettings.id, updatedSettings);
  }

  propertyPreferenceOnChange = (e) => {
    const { checked, name } = event.target;

    const updatedProperty = {
      ...this.props.property,
      preferences: {
        ...this.props.property.preferences,
        [name]: checked,
      },
    };

    this.props.onPropertyChanged(updatedProperty);
  };

  requireUnitSchedulingChange(event) {
    const updateRequireUnitScheduling = event.target.checked;

    const updatedProperty = this.props.property;

    updatedProperty.preferences.require_unit_scheduling =
      updateRequireUnitScheduling;

    this.props.onPropertyChanged(updatedProperty);
  }

  virtualTourLinksChange(event) {
    const virtualTourLinksFlag = event.target.checked;

    const updatedSettings = { ...this.props.schedulingSettings };

    updatedSettings.preferences.virtual_tour_links = virtualTourLinksFlag;

    this.props.onSchedulingChanged(
      this.props.doorwaySettings.id,
      updatedSettings.preferences
    );
  }

  virtualTourLinksMappingChange(updatedData) {
    const updatedSettings = { ...this.props.schedulingSettings };

    updatedSettings.preferences.virtual_tour_links_mapping = updatedData;

    this.props.onSchedulingChanged(
      this.props.doorwaySettings.id,
      updatedSettings.preferences
    );
  }
}

export default PropertyDoorway;

PropertyDoorway.contextType = FeatureFlagContext;

PropertyDoorway.propTypes = {
  doorwaySettings: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    website: PropTypes.string,
    settings: PropTypes.shape({
      hours: PropTypes.object,
      message_templates: PropTypes.object,
      snippetConfig: PropTypes.object,
      preferences: PropTypes.shape({
        button_color: PropTypes.string,
        button_greeting: PropTypes.string,
        display_launcher_button: PropTypes.bool,
        emit_analytics_events: PropTypes.bool,
        emit_tag_manager_events: PropTypes.bool,
        primary_color: PropTypes.string,
        recaptcha_public_key: PropTypes.string,
        recaptcha_score_threshold: PropTypes.string,
      }),
    }),
  }),
  isChatEnabled: PropTypes.bool,
  mergeTags: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onDoorwayChanged: PropTypes.func,
  onPropertyChanged: PropTypes.func,
  onSchedulingChanged: PropTypes.func,
  property: PropTypes.shape({
    preferences: PropTypes.object,
  }),
  schedulingSettings: PropTypes.shape({
    preferences: PropTypes.shape({
      virtual_tour_links: PropTypes.bool,
      virtual_tour_links_mapping: PropTypes.array,
    }),
  }),
  fetchNearbyProperties: PropTypes.func,
  teamProperties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      distance: PropTypes.number,
    }),
  ),
};
